/**
 * Hook for Balance Account selection with auto-select for single account
 */
import { useModel, useRequest } from '@umijs/max';
import { useEffect, useState } from 'react';
import { getPartnerBalanceAccounts } from '@/services/business/partner';

interface UseBalanceAccountSelectorOptions {
  /** Auto-select first account if only one exists */
  autoSelectSingle?: boolean;
}

interface UseBalanceAccountSelectorResult {
  /** Currently selected balance account ID */
  selectedAccountId: string | undefined;
  /** Set selected account ID */
  setSelectedAccountId: (id: string | undefined) => void;
  /** List of balance accounts */
  balanceAccounts: BusinessAPI.BalanceAccount[];
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: Error | undefined;
  /** Whether auto-selected (single account) */
  isAutoSelected: boolean;
  /** Refresh balance accounts list */
  refresh: () => void;
}

export function useBalanceAccountSelector(
  options: UseBalanceAccountSelectorOptions = {},
): UseBalanceAccountSelectorResult {
  const { autoSelectSingle = true } = options;
  const { currentMerchant } = useModel('merchant');
  const [selectedAccountId, setSelectedAccountId] = useState<string>();
  const [isAutoSelected, setIsAutoSelected] = useState(false);

  const { data, loading, error, refresh } = useRequest(
    () => getPartnerBalanceAccounts(currentMerchant?.id || 0),
    {
      ready: !!currentMerchant?.id,
      refreshDeps: [currentMerchant?.id],
    },
  ) as {
    data: BusinessAPI.BalanceAccount[] | undefined;
    loading: boolean;
    error: Error | undefined;
    refresh: () => void;
  };

  const balanceAccounts: BusinessAPI.BalanceAccount[] = data || [];

  // Reset selection when merchant changes
  useEffect(() => {
    setSelectedAccountId(undefined);
    setIsAutoSelected(false);
  }, [currentMerchant?.id]);

  // Auto-select if only one balance account exists
  useEffect(() => {
    if (
      autoSelectSingle &&
      !loading &&
      balanceAccounts.length === 1 &&
      !selectedAccountId
    ) {
      setSelectedAccountId(balanceAccounts[0].adyenBalanceAccountId);
      setIsAutoSelected(true);
    }
  }, [autoSelectSingle, loading, balanceAccounts, selectedAccountId]);

  return {
    selectedAccountId,
    setSelectedAccountId: (id) => {
      setSelectedAccountId(id);
      setIsAutoSelected(false);
    },
    balanceAccounts,
    loading,
    error: error as Error | undefined,
    isAutoSelected,
    refresh,
  };
}

export default useBalanceAccountSelector;
